/** Feature configuration for Rush Fever 7s Deluxe */

export interface FreeGameConfig {
  /** Number of free spins awarded */
  spinsAwarded: number;
  /** Whether free games can retrigger */
  canRetrigger: boolean;
  /** Rush Fever prize multiplier during free games */
  rushPrizeMultiplier: number;
  /** Number of reels during free games (no reel 6) */
  reelCount: number;
}

export interface RushFeverPrize {
  /** Number of Rush symbols collected */
  rushCount: number;
  /** Prize multiplier (times total bet) */
  multiplier: number;
}

export interface JackpotTier {
  /** Tier name */
  name: string;
  /** Prize multiplier */
  multiplier: number;
  /** Weight for random selection */
  weight: number;
}

export interface BuyAFeatureConfig {
  /** Cost multiplier (times total bet) */
  costMultiplier: number;
}

/** Free game constants */
export const FREE_GAME_SPINS = 12;
export const FREE_GAME_RETRIGGER = false;
export const FREE_GAME_REEL_COUNT = 5;
export const RUSH_FEVER_FREE_GAME_MULTIPLIER = 2;

/**
 * Escalating WILD weight tiers for each free spin (1-indexed, 12 tiers).
 * Format: [wildWeight, notWildWeight]
 * Probability = wildWeight / (wildWeight + notWildWeight)
 * Spin 1 ≈ 1.5%, Spin 12 ≈ 18.2%
 */
export const ESCALATING_WILD_WEIGHTS: readonly [number, number][] = [
  [180,  11659],  // Spin 1:  ~1.52%
  [360,  11479],  // Spin 2:  ~3.04%
  [540,  11299],  // Spin 3:  ~4.56%
  [720,  11119],  // Spin 4:  ~6.08%
  [900,  10939],  // Spin 5:  ~7.60%
  [1080, 10759],  // Spin 6:  ~9.12%
  [1260, 10579],  // Spin 7:  ~10.65%
  [1440, 10399],  // Spin 8:  ~12.16%
  [1620, 10219],  // Spin 9:  ~13.68%
  [1800, 10039],  // Spin 10: ~15.21%
  [1980, 9859],   // Spin 11: ~16.73%
  [2160, 9679],   // Spin 12: ~18.25%
];

export const FREE_GAME_CONFIG: FreeGameConfig = {
  spinsAwarded: FREE_GAME_SPINS,
  canRetrigger: FREE_GAME_RETRIGGER,
  rushPrizeMultiplier: RUSH_FEVER_FREE_GAME_MULTIPLIER,
  reelCount: FREE_GAME_REEL_COUNT,
};

/** Rush Fever prize table — multiplier applied to total bet */
export const RUSH_FEVER_PRIZES: readonly RushFeverPrize[] = [
  { rushCount: 3, multiplier: 1 },
  { rushCount: 4, multiplier: 2 },
  { rushCount: 5, multiplier: 7 },
  { rushCount: 6, multiplier: 20 },
  { rushCount: 7, multiplier: 50 },
  { rushCount: 8, multiplier: 200 },
  { rushCount: 9, multiplier: 2 },
];

/** Quick lookup: rush count -> bet multiplier */
export const RUSH_FEVER_PRIZE_MAP: Record<number, number> = {
  3: 1, 4: 2, 5: 7, 6: 20, 7: 50, 8: 200, 9: 2,
};

// TODO: Fill in actual jackpot tier weights from math model
export const JACKPOT_TIERS: readonly JackpotTier[] = [
  { name: 'Mini', multiplier: 10, weight: 100 },
  { name: 'Minor', multiplier: 25, weight: 50 },
  { name: 'Major', multiplier: 100, weight: 10 },
  { name: 'Grand', multiplier: 1000, weight: 1 },
];

export const BUY_A_FEATURE: BuyAFeatureConfig = {
  costMultiplier: 80,
};

// ─── Jackpot Pick Configuration ─────────────────────────────────────────────

/**
 * Base game trigger weights for jackpot pick (3 independent checks).
 * Format: [triggerWeight, noTriggerWeight]
 * Probability = triggerWeight / (triggerWeight + noTriggerWeight)
 */
export const JACKPOT_PICK_BASE_WEIGHTS: readonly [number, number][] = [
  [128, 11891],  // ~1.07%
  [256, 11763],  // ~2.13%
  [384, 11635],  // ~3.20%
];

/** Tier multipliers: tier number -> bet multiplier */
export const JACKPOT_PICK_TIER_MULTIPLIERS: Record<number, number> = {
  6: 20,
  7: 50,
  8: 200,
  9: 1000,
};

/** Tier labels for display */
export const JACKPOT_PICK_TIER_NAMES: Record<number, string> = {
  6: 'RUSH FEVER 6',
  7: 'RUSH FEVER 7',
  8: 'RUSH FEVER 8',
  9: 'RUSH FEVER 9',
};

/** Tier colors for UI display */
export const JACKPOT_PICK_TIER_COLORS: Record<number, number> = {
  6: 0x3399ff,  // blue
  7: 0x33cc66,  // green
  8: 0x9933cc,  // purple
  9: 0xffcc00,  // gold
};

/** Weights for picks 1 and 2 — equal probability per tier (6,7,8,9) */
export const JACKPOT_PICK_12_WEIGHTS: readonly number[] = [2500, 2500, 2500, 2500];

/** Weights for pick 3 — heavily biased toward tier 6 */
export const JACKPOT_PICK_3_WEIGHTS: readonly number[] = [8994, 966, 32, 8];

/** The tier values corresponding to weight indices */
export const JACKPOT_PICK_TIERS: readonly number[] = [6, 7, 8, 9];
